/**
 * Which chunks a camera view needs (ADR-019 consequences, ADR-088 decision 1): the chunks intersecting the view rectangle plus
 * one chunk of margin on every side, clamped to the map's chunk grid.
 */

/**
 * An inclusive chunk range.
 */
export interface ChunkRange {
  readonly minCx: number;
  readonly minCy: number;
  readonly maxCx: number;
  readonly maxCy: number;
}

/**
 * A chunk range a caller keeps and has filled in place, so asking every frame allocates nothing.
 */
export interface ChunkRangeOut {
  minCx: number;
  minCy: number;
  maxCx: number;
  maxCy: number;
}

/**
 * A view rectangle in world pixels.
 */
export interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Clamps a value into `[low, high]`.
 *
 * @param value Value.
 * @param low Lower bound.
 * @param high Upper bound.
 * @returns The clamped value.
 */
function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

/**
 * Computes the chunk range a view needs. The last chunk touched by the view is `ceil(edge / pixels) - 1`,
 * so `ceil(edge / pixels)` is exactly one chunk of margin whether or not the edge lands on a boundary.
 *
 * @param view View rectangle in world pixels.
 * @param tileSize Tile size in pixels.
 * @param chunkSize Chunk size in tiles.
 * @param columns Chunk columns in the map.
 * @param rows Chunk rows in the map.
 * @param out A range to fill and return in place of a new one, for a caller asking every frame.
 * @returns The inclusive range, always inside the grid.
 */
export function visibleChunks(view: ViewRect, tileSize: number, chunkSize: number, columns: number, rows: number, out?: ChunkRangeOut): ChunkRange {
  const pixels = tileSize * chunkSize;
  const range = out ?? { minCx: 0, minCy: 0, maxCx: 0, maxCy: 0 };
  range.minCx = clamp(Math.floor(view.left / pixels) - 1, 0, columns - 1);
  range.minCy = clamp(Math.floor(view.top / pixels) - 1, 0, rows - 1);
  range.maxCx = clamp(Math.ceil((view.left + view.width) / pixels), 0, columns - 1);
  range.maxCy = clamp(Math.ceil((view.top + view.height) / pixels), 0, rows - 1);
  return range;
}
